import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const PATHOLOGY_API_URL = process.env.PATHOLOGY_API_URL;

class PathologyAPIService {

    async getHeaders() {
        const res = await fetchAuthSession();
        const config = {
            headers: {
                'Content-Type': 'application/json',
                authorizationToken: `${res.tokens.idToken}`,
            },
        };
        return config;
    }

    async getPathologyStats() {
        const header = await this.getHeaders();
        return axios.get(`${PATHOLOGY_API_URL}pathology/stats`, header).then(res => res.data);
    }

    async createPathologyRecord(patientId: string, data?: {
        priority?: string;
        accessionNumber?: string;
        specimenType?: string;
        grossDescription?: string;
    }) {
        const header = await this.getHeaders();
        return axios
            .post(`${PATHOLOGY_API_URL}pathology/create`, { patientId, ...data }, header)
            .then(res => res.data);
    }

    async generatePathologyPresignedUrl(uploadId: string, totalFiles: number, fileType: string) {
        const header = await this.getHeaders();
        return axios
            .post(
                `${PATHOLOGY_API_URL}pathology/generate-presigned-url`,
                { uploadId, totalFiles, fileType },
                header
            )
            .then(res => res.data);
    }

    async completePathologyUpload(uploadId: string) {
        const header = await this.getHeaders();
        return axios
            .post(`${PATHOLOGY_API_URL}pathology/complete`, { uploadId }, header)
            .then(res => res.data);
    }

    async getPathologyImportStatus(uploadId: string) {
        const header = await this.getHeaders();
        return axios
            .post(`${PATHOLOGY_API_URL}pathology/get-import-status`, { uploadId }, header)
            .then(res => res.data);
    }

    async getPathologyStudies(params?: { limit?: number; start_date?: string; end_date?: string; next_token?: string; mrn?: string; patientName?: string }) {

        const header = await this.getHeaders();
        return axios
            .get(`${PATHOLOGY_API_URL}pathology/slide-library`, header)
            .then(res => res.data);
    }

    async getPathologyReportPresignedUrl(studyId: string) {
        const header = await this.getHeaders();
        return axios
            .post(`${PATHOLOGY_API_URL}pathology/report/presigned-url`, { studyId }, header)
            .then(res => res.data);
    }

    async getPathologyReport(studyId: string, reportId?: string) {
        const header = await this.getHeaders();
        let url = `${PATHOLOGY_API_URL}pathology/report?studyId=${encodeURIComponent(studyId)}`;
        if (reportId) {
            url += `&reportId=${encodeURIComponent(reportId)}`;
        }
        return axios.get(url, header).then(res => res.data);
    }

    async updatePathologyReportPresignedUrl(studyId: string, reportId: string) {
        const header = await this.getHeaders();
        return axios
            .post(
                `${PATHOLOGY_API_URL}pathology/report/update-presigned-url`,
                { studyId, reportId },
                header
            )
            .then(res => res.data);
    }

    async deletePathologyReport(studyId: string, reportId: string) {
        const header = await this.getHeaders();
        return axios
            .delete(
                `${PATHOLOGY_API_URL}pathology/report?studyId=${encodeURIComponent(studyId)}&reportId=${encodeURIComponent(reportId)}`,
                header
            )
            .then(res => res.data);
    }

    async uploadFileToPresignedUrl(presignedUrl: string, file: File, onProgress?: (progress: number) => void) {
        const formData = new FormData();

        if (typeof presignedUrl === 'object' && presignedUrl !== null) {
            const presignedData = presignedUrl as any;
            if (presignedData.fields) {
                Object.entries(presignedData.fields).forEach(([key, value]) => {
                    formData.append(key, String(value));
                });
                formData.append('file', file);

                return axios.post(presignedData.url, formData, {
                    onUploadProgress: (progressEvent) => {
                        if (onProgress && progressEvent.total) {
                            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            onProgress(percentCompleted);
                        }
                    },
                });
            }
        }

        return axios.put(presignedUrl, file, {
            headers: {
                'Content-Type': file.type || 'application/octet-stream',
            },

            onUploadProgress: (progressEvent) => {
                if (onProgress && progressEvent.total) {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(percentCompleted);
                }
            },
        });
    }

    async updateSlideLibraryStatus(orderId: string, slideLibraryStatus: string) {
        const header = await this.getHeaders();
        return axios
            .patch(
                `${PATHOLOGY_API_URL}pathology/orders/${encodeURIComponent(orderId)}/status`,
                { slideLibraryStatus },
                header,
            )
            .then(res => res.data);
    }
}

const PathologyServices = new PathologyAPIService();
export default PathologyServices;
